import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../lib/hooks/useToast';
import { supabase } from '../lib/supabase';
import { useSettingsStore } from '../lib/stores/settingsStore';
import { OAuthCallbackLayout } from '../components/oauth/OAuthCallbackLayout';
import { dispatchTaskCompleted } from '../lib/onboarding/dispatchTaskCompleted';

export function FattureInCloudCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { settings } = useSettingsStore();
  const [processingCallback, setProcessingCallback] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Discrimina flow superadmin (Flusia stessa) da flow CRM cliente per
  // scegliere il redirect corretto post-callback (e bottoni success/error).
  const [isSuperFlow, setIsSuperFlow] = useState(false);
  const processedRef = useRef(false);
  const stateUsedRef = useRef(false);
  const completedRef = useRef(false);

  // Path target post-callback in base al flow
  const successPath = isSuperFlow
    ? '/admin/fattureincloud-flusia?fattureincloud_result=success'
    : '/settings?tab=fattureincloud&fattureincloud_result=success';
  const fallbackPath = isSuperFlow
    ? '/admin/fattureincloud-flusia'
    : '/settings?tab=fattureincloud';

  useEffect(() => {
    // Prevent double processing in React 18 strict mode
    if (processedRef.current) {
      console.log('[FattureInCloudCallback] Already processed, skipping');
      return;
    }
    
    processedRef.current = true;
    console.log('[FattureInCloudCallback] Component mounted, parsing query params');
    console.log('[FattureInCloudCallback] URL search params:', location.search);
    
    const processCallback = async () => {
      // Exit early if already completed
      if (completedRef.current) {
        console.log('[FattureInCloudCallback] Already completed, exiting');
        return;
      }
      
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const errorParam = params.get('error');
        const errorDescription = params.get('error_description');
        
        // Log all URL parameters for debugging
        console.log('[FattureInCloudCallback] All URL parameters:', {
          code,
          state,
          error: errorParam,
          error_description: errorDescription,
          all_params: Object.fromEntries(params.entries())
        });
        
        if (errorParam) {
          console.error('[FattureInCloudCallback] Error from OAuth provider:', errorParam);
          
          // Special handling for scope errors
          if (errorParam === 'invalid_scope' || errorDescription?.includes('scope')) {
            console.error('[FattureInCloudCallback] Scope validation error - please check the scopes configuration in the OAuth setup');
            console.error('[FattureInCloudCallback] Full error description:', decodeURIComponent(errorDescription || ''));
            setError(`Errore di validazione degli scope: ${errorDescription}`);
            toast.error(`Errore negli scope richiesti. Controlla la configurazione dell'applicazione.`);
          } else {
            // General error handling
            const displayErrorDescription = errorDescription || 'Errore durante l\'autorizzazione';
            setError(displayErrorDescription);
            toast.error(`Errore: ${displayErrorDescription}`);
          }
          
          setTimeout(() => navigate('/settings?tab=fattureincloud', { replace: true }), 3000);
          return;
        }
        
        if (!code || !state) {
          console.error('[FattureInCloudCallback] Missing required parameters:', { code, state });
          setError('Parametri mancanti nella risposta');
          toast.error('Errore: parametri mancanti nella risposta');
          setTimeout(() => navigate('/settings?tab=fattureincloud', { replace: true }), 3000);
          return;
        }

        // ── Branching: state con prefisso "super_" → flusso superadmin Flusia ──
        // Il redirect URI è lo stesso del modulo CRM (per evitare di registrare un
        // secondo URI in dashboard FIC) ma il flusso è separato: token va in
        // fattureincloud_superadmin_config, non in fattureincloud_configs.
        if (state.startsWith('super_')) {
          console.log('[FattureInCloudCallback] Detected superadmin flow');
          setIsSuperFlow(true);
          completedRef.current = true;

          try {
            const { data: superData, error: superErr } = await supabase.functions.invoke(
              'fattureincloud-superadmin-auth',
              { body: { action: 'exchange', code } }
            );

            if (superErr) throw new Error(superErr.message);
            if (superData?.error) throw new Error(superData.error);

            toast.success('Fatture in Cloud Flusia connesso con successo!');
            // Navigate immediato (replace) — non settiamo processingCallback=false
            // per evitare un frame intermedio dove vedi il success layout fallback
            // con il bottone hardcoded a /settings.
            navigate('/admin/fattureincloud-flusia?fattureincloud_result=success', { replace: true });
          } catch (e: any) {
            console.error('[FattureInCloudCallback] Superadmin exchange failed:', e);
            setError(`Errore connessione superadmin: ${e.message}`);
            setProcessingCallback(false);
            toast.error('Errore durante la connessione superadmin');
            setTimeout(() => navigate('/admin/fattureincloud-flusia', { replace: true }), 3000);
          }
          return;
        }

        console.log('[FattureInCloudCallback] Validating state');
        
        // Validate the state from the database
        const { data: stateData, error: stateError } = await supabase
          .from('oauth_states')
          .select('id, organization_id, is_used, expires_at')
          .eq('state', state)
          .single();
        
        if (stateError || !stateData) {
          console.error('[FattureInCloudCallback] Invalid state:', stateError || 'No state found');
          setError('Sessione non valida');
          toast.error('Errore: sessione non valida');
          setTimeout(() => navigate('/settings?tab=fattureincloud', { replace: true }), 3000);
          return;
        }
        
        // Check if the state is expired or already used
        const now = new Date();
        if (new Date(stateData.expires_at) < now) {
          console.error('[FattureInCloudCallback] State expired:', { 
            expires_at: stateData.expires_at
          });
          setError('Sessione scaduta');
          toast.error('Errore: sessione scaduta');
          setTimeout(() => navigate('/settings?tab=fattureincloud', { replace: true }), 3000);
          return;
        }
        
        // Check if we've already used this state in this session to prevent double processing
        if (stateData.is_used && stateUsedRef.current) {
          console.error('[FattureInCloudCallback] State already used in this session');
          setError('Sessione già utilizzata');
          toast.error('Errore: sessione già utilizzata');
          setTimeout(() => navigate('/settings?tab=fattureincloud', { replace: true }), 3000);
          return;
        }
        
        // Mark the state as used to prevent replay attacks
        // But only if it hasn't been used yet
        if (!stateData.is_used) {
          const { error: updateError } = await supabase
            .from('oauth_states')
            .update({ is_used: true })
            .eq('id', stateData.id);
            
          if (updateError) {
            console.error('[FattureInCloudCallback] Error marking state as used:', updateError);
            // Continue anyway, this is not critical
          }
        }
        
        stateUsedRef.current = true;
        
        console.log('[FattureInCloudCallback] State validated, exchanging token');
        
        // Exchange the code for access token directly in the client
        console.log('[FattureInCloudCallback] Attempting direct exchange of authorization code for token');
        
        console.log('[FattureInCloudCallback] Exchanging code for token using edge function');
        
        // Call the edge function to exchange the code for tokens
        const response = await supabase.functions.invoke('fattureincloud-token-exchange', {
          body: {
            code,
            state,
            organization_id: stateData.organization_id
          }
        });
        
        console.log('[FattureInCloudCallback] Response from token exchange:', response);
        
        // Handle response - check for errors first
        if (response?.error) {
          console.error('[FattureInCloudCallback] Token exchange error:', response.error);
          const errorMessage = response.error?.message || 'Errore sconosciuto';
          setError(`Errore durante lo scambio del token: ${errorMessage}`);
          toast.error('Errore durante lo scambio del token');
          setTimeout(() => navigate('/settings?tab=fattureincloud', { replace: true }), 3000);
          return;
        }
        
        // Check for success case
        if (response?.data?.success === true) {
          console.log('[FattureInCloudCallback] DEBUG: Success detected, redirecting');

          // Mark as completed FIRST to prevent any further execution
          completedRef.current = true;

          setProcessingCallback(false);
          toast.success('Connessione a Fatture in Cloud completata con successo!');
          dispatchTaskCompleted('integrations', 'fic_oauth_success');
          navigate('/settings?tab=fattureincloud&fattureincloud_result=success', { replace: true });
          return;
        }
        
        // If we get here, something unexpected happened
        console.error('[FattureInCloudCallback] Unexpected response structure:', response);
        setError('Risposta non valida dal server');
        toast.error('Errore: risposta non valida dal server');
        setTimeout(() => navigate('/fattureincloud', { replace: true }), 3000);
      } catch (error) {
        // Exit if already completed to prevent error handling after success
        if (completedRef.current) {
          console.log('[FattureInCloudCallback] Completed flag set, ignoring error:', error);
          return;
        }
        
        console.error('[FattureInCloudCallback] Unexpected error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
        setError(`Errore durante l'elaborazione del callback: ${errorMessage}`);
        toast.error('Errore durante l\'elaborazione del callback');
        setProcessingCallback(false);
        setTimeout(() => navigate('/fattureincloud', { replace: true }), 3000);
      }
    };

    processCallback();
  }, [navigate, location.search, toast, settings]);

  if (processingCallback) {
    return (
      <OAuthCallbackLayout
        status="processing"
        title="Connessione in corso"
        description="Stiamo completando la connessione con Fatture in Cloud."
        footerNote="Verrai reindirizzato automaticamente al termine."
      />
    );
  }

  if (error) {
    return (
      <OAuthCallbackLayout
        status="error"
        title="Connessione non riuscita"
        description={error}
        primaryAction={{
          label: isSuperFlow ? 'Torna a Fatturazione Flusia' : 'Torna alle impostazioni',
          onClick: () => navigate(fallbackPath, { replace: true }),
        }}
        footerNote="Verrai reindirizzato automaticamente."
      />
    );
  }

  return (
    <OAuthCallbackLayout
      status="success"
      title="Connessione completata"
      description={
        isSuperFlow
          ? 'Fatture in Cloud Flusia è connesso. Ora puoi configurare i defaults dalla pagina superadmin.'
          : 'La connessione con Fatture in Cloud è attiva. Ora puoi gestire fatture, anagrafiche e scadenze dal CRM.'
      }
      primaryAction={{
        label: isSuperFlow ? 'Vai a Fatturazione Flusia' : 'Vai alle impostazioni',
        onClick: () => navigate(successPath, { replace: true }),
      }}
    />
  );
};

// Mantenere sia l'esportazione di default che quella nominata per compatibilità
export default FattureInCloudCallback;